import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../contexts/NotificationContext';
import Loading from '../common/Loading';
import AvatarUpload from '../common/AvatarUpload';
import Button from '../common/Button';

const Users = () => {
  const { userProfile } = useAuth();
  const { notify } = useNotification();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Fetched users:', usersData);
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        notify.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [notify]);

  const handleAvatarUpdate = (userId, avatarUrl) => {
    setUsers(prev => prev.map(user => 
      user.id === userId ? { ...user, avatarUrl } : user
    ));
  };

  const handleRoleChange = async (userId, newRole) => {
    console.log('Changing role for user:', userId, 'to:', newRole);
    
    if (userId === userProfile?.uid) {
      notify.error('You cannot change your own role');
      return;
    }

    try {
      setUpdating(true);
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date().toISOString()
      });
      
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      
      notify.success(`User role updated to ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      notify.error('Failed to update user role: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === userProfile?.uid) {
      notify.error('You cannot delete your own account');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers(prev => prev.filter(user => user.id !== userId));
      notify.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      notify.error('Failed to delete user');
    }
  };

  if (loading) {
    return <Loading text="Loading users..." />;
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
          Users
        </h1>
        <div className="flex items-center space-x-4">
          <span className="text-sm lg:text-base text-gray-500 dark:text-gray-400">
            {users.length} user{users.length !== 1 ? 's' : ''}
          </span>
          {userProfile?.role === 'admin' && (
            <span className="px-2 lg:px-3 py-1 text-xs lg:text-sm bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 rounded-full">
              Admin
            </span>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {users.map(user => (
              <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <AvatarUpload
                        userId={user.id}
                        currentAvatarUrl={user.avatarUrl}
                        displayName={user.displayName}
                        onAvatarUpdate={(url) => handleAvatarUpdate(user.id, url)}
                        size="small"
                        showRemoveButton={false}
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.displayName || 'Unknown User'}
                        {user.id === userProfile?.uid && (
                          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {user.email || 'No email'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <select
                      value={user.role || 'user'}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      disabled={user.id === userProfile?.uid || updating}
                      className="text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[120px]"
                    >
                      <option value="user">User</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                    {!user.role && (
                      <span className="text-xs text-red-500">No role set</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {user.id !== userProfile?.uid && (
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 px-3 py-1 rounded border border-red-300 hover:bg-red-50 dark:border-red-600 dark:hover:bg-red-900/20 transition-colors"
                      disabled={updating}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {users.map(user => (
          <div key={user.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <AvatarUpload
                  userId={user.id}
                  currentAvatarUrl={user.avatarUrl}
                  displayName={user.displayName}
                  onAvatarUpdate={(url) => handleAvatarUpdate(user.id, url)}
                  size="small"
                  showRemoveButton={false}
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {user.displayName || 'Unknown User'}
                    </h3>
                    {user.id === userProfile?.uid && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded">
                        You
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {user.email || 'No email'}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-500 dark:text-gray-400">Role:</span>
                <select
                  value={user.role || 'user'}
                  onChange={(e) => handleRoleChange(user.id, e.target.value)}
                  disabled={user.id === userProfile?.uid || updating}
                  className="w-full mt-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <span className="font-medium text-gray-500 dark:text-gray-400">Joined:</span>
                <p className="mt-1 text-gray-900 dark:text-white">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
            </div>

            {user.id !== userProfile?.uid && (
              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => handleDeleteUser(user.id)}
                  disabled={updating}
                  className="w-full"
                >
                  Delete User
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {users.length === 0 && (
        <div className="text-center py-8 lg:py-12">
          <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No users found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            User profiles will appear here once they are created.
          </p>
        </div>
      )}
    </div>
  );
};

export default Users;